import hydrateThreadMeta from '@/redux/actions/pieveActions/hydrateThreadMeta.js';
import applyAssistantDelta from '@/redux/actions/pieveActions/applyAssistantDelta.js';
import setThreadRunState from '@/redux/actions/pieveActions/setThreadRunState.js';
import setThreadWebSearchState from '@/redux/actions/pieveActions/setThreadWebSearchState.js';
import upsertThreadEvent from '@/redux/actions/pieveActions/upsertThreadEvent.js';
import setThreadDiffCard from '@/redux/actions/pieveActions/setThreadDiffCard.js';
import deleteThreadEvent from '@/redux/actions/pieveActions/deleteThreadEvent.js';
import setThreadContextWindowUsage from '@/redux/actions/pieveActions/setThreadContextWindowUsage.js';
import upsertThreadOptimistic from '@/redux/actions/pieveActions/upsertThreadOptimistic.js';
import persistThreadOpenedState from '@/redux/actions/pieveActions/threadMutations/persistThreadOpenedState.js';
import { activeThreadId as selectActiveThreadId } from '@/redux/selectors/pieveSelectors/activeThreadIdSelector.js';
import normalizeContextWindowUsage from '@/lib/contextWindow/normalizeContextWindowUsage.js';
import normalizePieveThreadEventForPresentation from '@/lib/pieveThreadEventNormalization.js';
import normalizeRemoteThreadEventForPresentation from '@/lib/threadEvents/normalizeRemoteThreadEventForPresentation.js';
import handleToolResultEvent from '@/redux/actions/pieveActions/handleThreadRuntimeEvent/handleToolResultEvent.js';
import handleWorkflowTestUpdatedEvent from '@/redux/actions/pieveActions/handleThreadRuntimeEvent/handleWorkflowTestUpdatedEvent.js';

const getRunStatePayload = (payload = {}) => {
  const root = payload && typeof payload === 'object' ? payload : {};
  const data = root?.data && typeof root.data === 'object' ? root.data : {};
  const run = root?.run && typeof root.run === 'object' ? root.run : {};

  return {
    runId: root.runId || data.runId || run.runId || run.id || null,
    state: root.state || data.state || run.state || null,
    blockerCode: root.blockerCode || data.blockerCode || run.blockerCode || null,
    blockerReason: root.blockerReason || data.blockerReason || run.blockerReason || null,
    blockerMessage: root.blockerMessage || data.blockerMessage || run.blockerMessage || null,
    startedAt: root.startedAt || data.startedAt || run.startedAt || null,
    completedAt: root.completedAt || data.completedAt || run.completedAt || null,
    durationMs: root.durationMs ?? data.durationMs ?? run.durationMs ?? null,
    activeDurationMs: root.activeDurationMs ?? data.activeDurationMs ?? run.activeDurationMs ?? null,
    blockedDurationMs: root.blockedDurationMs ?? data.blockedDurationMs ?? run.blockedDurationMs ?? 0
  };
};

const getContextWindowUsagePayload = (payload = {}) => {
  const root = payload && typeof payload === 'object' ? payload : {};
  const data = root?.data && typeof root.data === 'object' ? root.data : {};
  return normalizeContextWindowUsage({
    model: root?.model ?? data?.model,
    inputTokens: root?.inputTokens ?? data?.inputTokens,
    outputTokens: root?.outputTokens ?? data?.outputTokens,
    totalTokens: root?.totalTokens ?? data?.totalTokens ?? root?.usedTokens ?? data?.usedTokens,
    realInputTokens: root?.realInputTokens ?? data?.realInputTokens,
    contextWindowTokens: root?.contextWindowTokens ?? data?.contextWindowTokens,
    autoCompactLimitTokens: root?.autoCompactLimitTokens ?? data?.autoCompactLimitTokens,
    remainingTokens: root?.remainingTokens ?? data?.remainingTokens,
    percentUsed: root?.percentUsed ?? data?.percentUsed
  });
};

const withInheritedRunId = ({ event, runId }) => {
  if (!event || typeof event !== 'object' || Array.isArray(event)) {
    return event;
  }

  const normalizedRunId = String(runId || '').trim();

  if (!normalizedRunId) {
    return event;
  }

  const normalizedEventRunId = String(event?.runId || '').trim();

  if (normalizedEventRunId) {
    return event;
  }

  return {
    ...event,
    runId: normalizedRunId
  };
};

const normalizeRuntimeEventsForPresentation = ({ event, runId }) => {
  const normalizedEvent = normalizeRemoteThreadEventForPresentation(
    withInheritedRunId({
      event,
      runId
    })
  );
  const normalizedEventList = Array.isArray(normalizedEvent) ? normalizedEvent : [normalizedEvent];

  return normalizedEventList
    .map((candidateEvent) => {
      return normalizePieveThreadEventForPresentation(candidateEvent);
    })
    .filter(Boolean);
};

const isAssistantPresentationEvent = ({ event = {}, rawEvent = null } = {}) => {
  const role = String(event?.payload?.role || event?.role || rawEvent?.payload?.role || rawEvent?.role || '')
    .trim()
    .toLowerCase();
  const className = String(event?.className || rawEvent?.className || '')
    .trim()
    .toLowerCase();
  return role === 'assistant' || className === 'assistant';
};

const acknowledgeVisibleAssistantEvent = ({ dispatch, getState, threadId, event, rawEvent = null }) => {
  const normalizedThreadId = String(threadId || '').trim();

  if (!normalizedThreadId || !isAssistantPresentationEvent({ event, rawEvent })) {
    return;
  }

  const activeThreadId = String(selectActiveThreadId(getState?.()) || '').trim();

  if (!activeThreadId || activeThreadId !== normalizedThreadId) {
    return;
  }

  const openedAt = new Date().toISOString();

  dispatch(
    upsertThreadOptimistic({
      thread: {
        id: normalizedThreadId,
        lastOpenedAt: openedAt
      }
    })
  );

  dispatch(
    persistThreadOpenedState({
      threadId: normalizedThreadId,
      openedAt
    })
  );
};

const handleThreadRuntimeEvent = (payload) => {
  return (dispatch, getState) => {
    const type = payload?.type;

    if (type === 'frontendIpcMarker') {
      return;
    }

    const threadId = payload?.threadId || payload?.data?.threadId || payload?.event?.threadId;

    if (!type || !threadId) {
      return;
    }

    if (type === 'threadEventAppended' || type === 'threadEventUpsert' || type === 'event.appended' || type === 'event.upsert') {
      const rawEvent = payload.event || payload.data?.event || payload.data;
      const currentSubmissionToken = getState()?.ui?.threadSurfaceSubmitRequest?.submissionToken || null;
      const normalizedEvents = normalizeRuntimeEventsForPresentation({
        event: rawEvent,
        runId: payload?.runId || payload?.data?.runId || payload?.run?.runId || payload?.run?.id || null
      });

      for (const normalizedEvent of normalizedEvents) {
        const isUserBubble = normalizedEvent?.type === 'bubble' && String(normalizedEvent?.className || '').includes('user');
        const eventWithProvenance = currentSubmissionToken && normalizedEvent && !isUserBubble ? { ...normalizedEvent, submissionToken: currentSubmissionToken } : normalizedEvent;

        dispatch(
          upsertThreadEvent({
            threadId,
            event: eventWithProvenance
          })
        );
        acknowledgeVisibleAssistantEvent({
          dispatch,
          getState,
          threadId,
          event: eventWithProvenance,
          rawEvent
        });
      }

      const rawEventType = String(rawEvent?.type || '').trim();
      if (rawEventType === 'tool_result') {
        handleToolResultEvent({ rawEvent, dispatch });
      }

      return;
    }

    if (type === 'threadEventDelete' || type === 'event.deleted') {
      dispatch(deleteThreadEvent({ threadId, eventId: payload.eventId || payload.id }));
      return;
    }

    if (type === 'assistantDelta' || type === 'assistant.delta') {
      dispatch(applyAssistantDelta({ threadId, eventId: payload.eventId || payload.assistantEventId, delta: payload.delta || payload.text, textOffset: payload.textOffset }));
      return;
    }

    if (type === 'threadRunState' || type === 'run.state') {
      const runStatePayload = getRunStatePayload(payload);
      dispatch(setThreadRunState({ threadId, ...runStatePayload }));
      return;
    }

    if (type === 'run.web_search' || type === 'threadWebSearch') {
      const root = payload && typeof payload === 'object' ? payload : {};
      const data = root?.data && typeof root.data === 'object' ? root.data : {};
      const status = String(root?.status || data?.status || '').trim() || null;
      const callsCompleted = root?.callsCompleted ?? data?.callsCompleted ?? null;
      const maxCalls = root?.maxCalls ?? data?.maxCalls ?? null;
      dispatch(setThreadWebSearchState({ threadId, status, callsCompleted, maxCalls }));
      return;
    }

    if (type === 'context.usage' || type === 'threadContextWindowUsage') {
      const contextWindowUsage = getContextWindowUsagePayload(payload);

      if (!contextWindowUsage) {
        return;
      }

      dispatch(setThreadContextWindowUsage({ threadId, contextWindowUsage }));
      return;
    }

    if (type === 'run.done') {
      const runStatePayload = getRunStatePayload(payload);
      dispatch(
        setThreadRunState({
          threadId,
          ...runStatePayload,
          state: runStatePayload?.state || 'complete'
        })
      );
      dispatch(
        hydrateThreadMeta({
          threadId
        })
      );
      return;
    }

    if (type === 'thread.updated') {
      dispatch(
        hydrateThreadMeta({
          threadId
        })
      );
      return;
    }

    if (type === 'workflow.updated') {
      if (payload?.data?.workflow) {
        dispatch({ type: 'GET_WORKFLOWS_SUCCESS', payload: { data: [payload.data.workflow] } });
      }

      return;
    }

    if (type === 'workflow.test.updated') {
      handleWorkflowTestUpdatedEvent({ payload, dispatch, getState });
      return;
    }

    if (type === 'threadDiffCard') {
      dispatch(setThreadDiffCard({ threadId, diffCard: payload.diffCard }));
    }
  };
};

export default handleThreadRuntimeEvent;
